import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "reactstrap";
import CustomDataTable from "components/customs/table";
import { ArrowLeft } from "react-feather";
import CommentsModal from "components/customs/comments-modal";
import DeclineModal from "components/customs/declineModal";
import { genericGet, genericPut } from "services/services.general";
import { defaultTableOption, rowPerPage } from "constants/table";
import { congRoles, canEditRoles } from "utility/constants";
import { ATTENDANCE_STATUS_OPTIONS } from "constants/status";
import Swal from "sweetalert2";
import styled from "styled-components";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import { HeaderText } from "assets/js/Styles";
import ConfirmModal from "./ConfirmModal";
import CongressmanDetailsCard from "../congressman/DetailsCard";
import eventColumns from "./columns";

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 90px;
`;

const CarSection = styled.div`
  background: #ffffff;
  border-radius: 15px;
  margin-top: 24px;
  box-shadow: 4px 9px 17px -1px #00000014;
`;

const CardContainer = styled.div`
  padding-top: 24px;
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 24px;
`;

const GoBackButton = styled(Button)`
  margin-right: 16px;
`;

const Main = () => {
  const { id, congID } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const repDetails = location?.state.repDetails || null;
  const congLogs = location?.state.congLogs || null;

  const [congDetails, setCongDetails] = useState({});

  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    currentRowsPerPage: rowPerPage,
  });
  const [hasAccess, setHasAccess] = useState(false);
  const [logs, setLogs] = useState([]);

  const [isModalShown, setIsModalShown] = useState(false);
  const [commentId, setCommentId] = useState(null);
  const [comments, setComments] = useState([]);
  const [isDeclineModalShown, setIsDeclineModalShown] = useState(false);
  const [isConfirmModalShown, setIsConfirmModalShown] = useState(false);

  const [congEvent, setCongEvent] = useState(null);
  const [eventTitle, setEventName] = useState(null);
  const [eveId, setEventId] = useState(0);

  const { user } = useAuth();

  const role = user?.type;

  const isRoleCongressman = congRoles.includes(role);

  const toggleModal = () => setIsModalShown(!isModalShown);
  const toggleDeclineModal = () => setIsDeclineModalShown(!isDeclineModalShown);
  const toggleConfirmModal = () => setIsConfirmModalShown(!isConfirmModalShown);

  const getStatusOptions = (status) => {
    let options = ATTENDANCE_STATUS_OPTIONS;

    if (!canEditRoles.includes(role)) {
      if (
        status === "Invited" ||
        status === "Declined" ||
        status === "Confirmed"
      ) {
        options = options?.filter(
          (option) =>
            option.value === "Confirmed" || option.value === "Declined"
        );
      }
    }

    return options;
  };

  const handleCloseModal = () => {
    toggleDeclineModal();
  };

  const handleCloseConfirmModal = () => {
    toggleConfirmModal();
  };

  const onSort = (column, sortDirection) => {
    setTableOption({
      ...tableOption,
      sort: {
        field: column.identifier,
        direction: sortDirection,
      },
    });
  };

  const fetchCongEvents = () => {
    genericGet({
      url: `congressman/${congID}/events`,
      params: {
        limit: tableOption.currentRowsPerPage,
        offset: tableOption.offset,
        column: tableOption.sort.field,
        order: tableOption.sort.direction,
        pos: role?.toLowerCase() === "cos" ? "cos" : null,
      },
      callback: (data) => {
        setTableOption({
          ...tableOption,
          data: data.rows,
          totalRows: data.count,
        });
      },
    });
  };

  const fetchComments = (eventId) => {
    genericGet({
      url: `/events/${eventId}/comments`,
      callback: (data) => {
        setComments(data);
      },
    });
  };

  const handleSaveStatusChange = (data) => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    genericPut({
      url: `/eventsparticipant/${data.EventParticipants[0].id}`,
      formData: {
        eventParticipantStatus:
          data?.EventParticipants[0].eventParticipantStatus,
      },
      callback: ({ isSuccess }) => {
        if (isSuccess) {
          fetchCongEvents();
        }
      },
    });
  };

  const handleStatusChange = async (index, option) => {
    const newData = [...tableOption.data];
    const oldValue = newData[index].EventParticipants[0].eventParticipantStatus;
    const newValue = option.value;

    const oldStatus = ATTENDANCE_STATUS_OPTIONS.find(
      (status) =>
        status.value ===
        newData[index].EventParticipants[0].eventParticipantStatus
    ).label;

    if (oldValue.toLowerCase() !== newValue.toLowerCase()) {
      try {
        if (newValue === "Declined") {
          newData[index] = {
            ...newData[index],
            EventParticipants: [
              {
                ...newData[index].EventParticipants[0],
                eventParticipantStatus: newValue,
              },
            ],
          };
          setEventName(newData[index].eventName);
          setEventId(newData[index].id);
          setCongEvent(newData[index].EventParticipants[0]);
          setIsDeclineModalShown(true);
        } else if (newValue === "Confirmed" && isRoleCongressman) {
          newData[index] = {
            ...newData[index],
            EventParticipants: [
              {
                ...newData[index].EventParticipants[0],
                eventParticipantStatus: newValue,
              },
            ],
          };
          setEventName(newData[index].eventName);
          setEventId(newData[index].id);
          setCongEvent(newData[index].EventParticipants[0]);
          setIsConfirmModalShown(true);
        } else {
          const result = await Swal.fire({
            title: "Warning",
            // eslint-disable-next-line max-len
            text: `Are you sure you want to change the status from ${oldStatus} to ${option.label}?`,
            icon: "warning",
            confirmButtonText: "OK",
            showCancelButton: true,
          });

          if (result.isConfirmed) {
            newData[index] = {
              ...newData[index],
              EventParticipants: [
                {
                  ...newData[index].EventParticipants[0],
                  eventParticipantStatus: newValue,
                },
              ],
            };

            setTableOption({
              ...tableOption,
              data: newData,
            });

            handleSaveStatusChange(newData[index], index);
          } else {
            newData[index] = {
              ...newData[index],
              eventParticipantStatus: oldValue,
            };

            setTableOption({
              ...tableOption,
              data: newData,
            });
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
  };

  const getIndex = (property) => {
    return logs?.findIndex((log) => log.data[property]);
  };

  const onChangePage = (page) => {
    setTableOption({
      ...tableOption,
      page,
      offset: tableOption.currentRowsPerPage * page - 10,
    });
  };

  const onChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
    setTableOption({
      ...tableOption,
      currentRowsPerPage,
      page: currentPage,
    });
  };

  const columns = eventColumns({
    handleStatusChange,
    getStatusOptions,
    isRoleCongressman,
    navigate,
    fetchComments,
    setCommentId,
    toggleModal,
    id,
    congDetails,
    congID,
  });

  const hasCongCosAccess = (congId) => {
    genericGet({
      url: `auth/has-access?congressman=${congId}`,
      callback: (data) => {
        setHasAccess(data.data);
      },
    });
  };

  const goBack = () => navigate(-1);

  useEffect(() => {
    if (hasAccess) {
      fetchCongEvents();
    }
  }, [
    hasAccess,
    tableOption.currentRowsPerPage,
    tableOption.offset,
    tableOption.sort.direction,
    tableOption.sort.field,
  ]);

  useEffect(() => {
    hasCongCosAccess(congID);
    setCongDetails(repDetails);
    setLogs(congLogs);
  }, [congID]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <div>
      <HeaderSection>
        <GoBackButton
          onClick={() => goBack()}
          aria-label="Go back"
          color="primary"
          outline
        >
          <ArrowLeft />
        </GoBackButton>

        <HeaderText>{"Events"}</HeaderText>
      </HeaderSection>

      <CongressmanDetailsCard
        congDetails={congDetails}
        logs={logs}
        getIndex={getIndex}
      />

      <CarSection>
        <CardContainer>
          <CustomDataTable
            columns={columns}
            data={tableOption.data}
            noHeader
            onSort={onSort}
            paginationPerPage={tableOption.currentRowsPerPage}
            paginationServer
            paginationTotalRows={tableOption.totalRows}
            progressPending={tableOption.isLoading}
            onChangePage={onChangePage}
            onChangeRowsPerPage={onChangeRowsPerPage}
          />
        </CardContainer>
      </CarSection>

      <CommentsModal
        isOpen={isModalShown}
        handleClose={toggleModal}
        comments={comments}
        fetchComments={fetchComments}
        congID={commentId}
        view="events"
        handleFetchTable={fetchCongEvents}
      />

      <DeclineModal
        isOpen={isDeclineModalShown}
        event={congEvent}
        handleClose={handleCloseModal}
        fetchComments={fetchComments}
        fetchDataTable={fetchCongEvents}
        eventName={eventTitle}
        eventId={eveId}
        view="rep"
      />

      <ConfirmModal
        isOpen={isConfirmModalShown}
        event={congEvent}
        handleClose={handleCloseConfirmModal}
        fetchComments={fetchComments}
        fetchDataTable={fetchCongEvents}
        eventName={eventTitle}
        eventId={eveId}
        view="rep"
      />
    </div>
  );
};

export default Main;
